import React, { useState, useMemo } from 'react';
import { cn } from '@/utils/cn';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { ChevronDown, ChevronRight, Users, AlertTriangle } from 'lucide-react';
import { formatTeamWeeks, formatPercent } from '@/utils/formatters';
import type { TeamCapacityResponse, TeamCapacity } from '@/types';

interface CapacityHeatmapProps {
  data: TeamCapacityResponse;
  onTeamClick?: (team: TeamCapacity) => void;
  className?: string;
}

/**
 * Returns the appropriate color class based on utilization percentage
 */
const getUtilizationColor = (percent: number): string => {
  if (percent >= 100) return 'bg-red-500/80';
  if (percent >= 90) return 'bg-amber-500/80';
  if (percent >= 80) return 'bg-yellow-500/60';
  if (percent >= 60) return 'bg-emerald-500/60';
  if (percent >= 40) return 'bg-emerald-400/50';
  return 'bg-emerald-300/40';
};

/**
 * Returns status label based on utilization
 */
const getUtilizationStatus = (percent: number): { label: string; variant: 'success' | 'warning' | 'danger' } => {
  if (percent >= 100) return { label: 'Over Capacity', variant: 'danger' };
  if (percent >= 90) return { label: 'Near Capacity', variant: 'warning' };
  if (percent >= 60) return { label: 'Optimal', variant: 'success' };
  return { label: 'Under Utilized', variant: 'warning' };
};

/**
 * Formats a number as team weeks with appropriate precision (for display without TW suffix)
 */
const formatTWValue = (value: number): string => {
  if (value >= 100) return Math.round(value).toString();
  return value.toFixed(1);
};

interface ProgramRowProps {
  programId: string;
  programName: string;
  totalCapacity: number;
  totalAllocated: number;
  utilizationPercent: number;
  teams: TeamCapacity[];
  onTeamClick?: (team: TeamCapacity) => void;
  initiallyExpanded?: boolean;
}

const ProgramRow: React.FC<ProgramRowProps> = ({
  programName,
  totalCapacity,
  totalAllocated,
  utilizationPercent,
  teams,
  onTeamClick,
  initiallyExpanded = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);
  const status = getUtilizationStatus(utilizationPercent);

  return (
    <div className="border-b border-[var(--border)] last:border-b-0">
      {/* Program Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-[var(--surface-muted)] transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-[var(--muted)]" />
          ) : (
            <ChevronRight className="w-4 h-4 text-[var(--muted)]" />
          )}
          <div>
            <div className="font-medium text-[var(--text)]">{programName}</div>
            <div className="text-xs text-[var(--muted)]">
              {teams.length} {teams.length === 1 ? 'team' : 'teams'}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Mini utilization bar */}
          <div className="w-24 hidden sm:block">
            <div className="h-2 bg-[var(--surface-muted)] rounded-full overflow-hidden">
              <div
                className={cn('h-full transition-all', getUtilizationColor(utilizationPercent))}
                style={{ width: `${Math.min(utilizationPercent, 100)}%` }}
              />
            </div>
          </div>

          <div className="text-right min-w-[80px]">
            <div className="text-sm font-medium text-[var(--text)]">
              {formatTWValue(totalAllocated)} / {formatTWValue(totalCapacity)}
            </div>
            <div className="text-xs text-[var(--muted)]">
              {formatPercent(utilizationPercent)}
            </div>
          </div>

          <Badge variant={status.variant} size="sm">
            {status.label}
          </Badge>
        </div>
      </button>

      {/* Teams Grid */}
      {isExpanded && (
        <div className="px-3 pb-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 pl-7">
            {teams.map((team) => (
              <TeamCell key={team.teamId} team={team} onClick={onTeamClick} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface TeamCellProps {
  team: TeamCapacity;
  onClick?: (team: TeamCapacity) => void;
}

const TeamCell: React.FC<TeamCellProps> = ({ team, onClick }) => {
  const utilizationColor = getUtilizationColor(team.utilizationPercent);
  const isOverCapacity = team.utilizationPercent >= 100;

  return (
    <button
      onClick={() => onClick?.(team)}
      className={cn(
        'relative p-2 rounded-lg border border-[var(--border)] transition-all',
        'hover:border-[var(--accent)] hover:shadow-md',
        'focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:ring-offset-2 focus:ring-offset-[var(--bg)]',
        onClick ? 'cursor-pointer' : 'cursor-default'
      )}
      title={`${team.teamName}: ${formatPercent(team.utilizationPercent)} utilization (${formatTWValue(team.allocatedWeeks)} / ${formatTWValue(team.availableWeeks)} TW)`}
    >
      {/* Utilization background */}
      <div
        className={cn(
          'absolute inset-0 rounded-lg transition-opacity',
          utilizationColor
        )}
        style={{ opacity: 0.15 + (team.utilizationPercent / 200) }}
      />

      <div className="relative">
        <div className="flex items-start justify-between gap-1">
          <div className="text-xs font-medium text-[var(--text)] truncate flex-1">
            {team.teamName}
          </div>
          {isOverCapacity && (
            <AlertTriangle className="w-3 h-3 text-status-error flex-shrink-0" />
          )}
        </div>

        <div className="mt-1">
          <div className="h-1.5 bg-[var(--surface-muted)] rounded-full overflow-hidden">
            <div
              className={cn('h-full transition-all', utilizationColor)}
              style={{ width: `${Math.min(team.utilizationPercent, 100)}%` }}
            />
          </div>
        </div>

        <div className="mt-1 flex items-baseline justify-between">
          <span className="text-xs text-[var(--muted)]">
            {formatTWValue(team.allocatedWeeks)} TW
          </span>
          <span className={cn(
            'text-xs font-medium',
            team.utilizationPercent >= 100 ? 'text-status-error' :
            team.utilizationPercent >= 90 ? 'text-status-warning' :
            'text-[var(--muted)]'
          )}>
            {formatPercent(team.utilizationPercent, { decimals: 0 })}
          </span>
        </div>
      </div>
    </button>
  );
};

const CapacityHeatmap: React.FC<CapacityHeatmapProps> = ({
  data,
  onTeamClick,
  className,
}) => {
  // Sort programs by utilization (highest first)
  const sortedPrograms = useMemo(() => {
    return [...data.byProgram].sort((a, b) => b.utilizationPercent - a.utilizationPercent);
  }, [data.byProgram]);

  // Calculate summary stats
  const stats = useMemo(() => {
    const overCapacity = data.teams.filter(t => t.utilizationPercent >= 100).length;
    const nearCapacity = data.teams.filter(t => t.utilizationPercent >= 80 && t.utilizationPercent < 100).length;
    const optimal = data.teams.filter(t => t.utilizationPercent >= 40 && t.utilizationPercent < 80).length;
    const underUtilized = data.teams.filter(t => t.utilizationPercent < 40).length;

    return { overCapacity, nearCapacity, optimal, underUtilized };
  }, [data.teams]);

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Capacity Utilization Heatmap
            </CardTitle>
            <CardDescription>
              Team capacity allocation by program ({data.summary.totalTeams} teams)
            </CardDescription>
          </div>

          {/* Legend */}
          <div className="hidden sm:flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-emerald-400/50" />
              <span className="text-[var(--muted)]">Under</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-emerald-500/60" />
              <span className="text-[var(--muted)]">Optimal</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-yellow-500/60" />
              <span className="text-[var(--muted)]">High</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-amber-500/80" />
              <span className="text-[var(--muted)]">Near</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-red-500/80" />
              <span className="text-[var(--muted)]">Over</span>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {/* Summary Stats */}
        <div className="px-4 pb-3 flex items-center gap-4 flex-wrap text-sm border-b border-[var(--border)]">
          <div className="flex items-center gap-2">
            <span className="text-[var(--muted)]">Avg Utilization:</span>
            <span className={cn(
              'font-medium',
              data.summary.averageUtilization >= 100 ? 'text-status-error' :
              data.summary.averageUtilization >= 80 ? 'text-status-warning' :
              'text-[var(--text)]'
            )}>
              {formatPercent(data.summary.averageUtilization)}
            </span>
          </div>
          <div className="text-[var(--muted)]">|</div>
          <div className="flex items-center gap-2">
            <span className="text-[var(--muted)]">Remaining:</span>
            <span className="font-medium text-[var(--text)]">
              {formatTeamWeeks(data.summary.remainingCapacity)}
            </span>
          </div>

          {stats.overCapacity > 0 && (
            <>
              <div className="text-[var(--muted)]">|</div>
              <div className="flex items-center gap-2 text-status-error">
                <AlertTriangle className="w-4 h-4" />
                <span>{stats.overCapacity} team{stats.overCapacity > 1 ? 's' : ''} over capacity</span>
              </div>
            </>
          )}
        </div>

        {/* Programs List */}
        <div className="divide-y divide-[var(--border)]">
          {sortedPrograms.length === 0 ? (
            <div className="p-8 text-center text-[var(--muted)]">
              No program data available
            </div>
          ) : (
            sortedPrograms.map((program, index) => (
              <ProgramRow
                key={program.programId}
                programId={program.programId}
                programName={program.programName}
                totalCapacity={program.totalCapacity}
                totalAllocated={program.totalAllocated}
                utilizationPercent={program.utilizationPercent}
                teams={program.teams}
                onTeamClick={onTeamClick}
                initiallyExpanded={index < 3} // Expand first 3 programs by default
              />
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CapacityHeatmap;
